import { captureGenericError } from 'src/lib/sentry';
import { PetState, PetSpecies, Pet } from 'src/replicant/buruburu/pet';
import { Attributes, PetState as ReduxPet } from '../pet/pet.redux';
import { Conversation } from 'src/replicant/conversation';
import { Visit } from 'src/replicant/visit';
import { State } from 'src/state';
import StateObserver from 'src/StateObserver';

type ConversationState = State['conversation'][string];
export type AppearanceParams = Pick<ConversationState, 'activeScript'> &
  Pick<ReduxPet, 'action'> &
  Pick<ReduxPet, 'species'> & {
    currentCareAction: boolean;
  } & ReduxPet['statusEffects'] &
  Attributes & { id: string };

export type AnimationState =
  | 'hang'
  | 'sleep'
  | 'eat'
  | 'toilet'
  | 'bath'
  | 'play'
  | 'angry'
  | 'joy'
  | 'surprised'
  | 'cry'
  | 'waive'
  | 'anxious'
  | 'evolve'
  | 'idle'
  | 'invited'
  | 'inviteNormal'
  | 'inviteUltimate'
  | 'accepted'
  | 'refusedNormal'
  | 'refusedUltimate'
  | 'ascension'
  | 'rocketIdle'
  | 'rocketLaunch'
  | 'shardIdle'
  | 'shardBox'
  | null;

function createAnimation(name: string) {
  return {
    ascension: `${name}_ascension`,
    hang: `${name}_hanging`,
    sleep: `${name}_sleep`,
    eat: `${name}_eat`,
    toilet: `${name}_toilet`,
    bath: `${name}_bath`,
    play: `${name}_play`,
    angry: `${name}_angry`,
    joy: `${name}_joy`,
    evolve: `${name}_idle_happy`,
    surprised: `${name}_surprised`,
    cry: `${name}_cry`,
    waive: `${name}_waive`,
    anxious: `${name}_anxious`,
    idle: `${name}_idle_normal`,
    accepted: `${name}_accepted`,
    refusedNormal: `${name}_refused_normalCoffee`,
    refusedUltimate: `${name}_refused_ulimateCoffee`,
    inviteNormal: `${name}_invite_normalCoffee`,
    inviteUltimate: `${name}_invite_ultimateCoffee`,
    invited: `${name}_invited`,
    rocketIdle: `${name}_rocket_idle`,
    rocketLaunch: `${name}_rocket_launch`,
    shardIdle: `${name}_shard_idle`,
    shardBox: `${name}_shard_box`,
    normal: {
      walking: `${name}_walk_normal`,
      idle: `${name}_idle_normal`,
    },
    happy: {
      walking: `${name}_walk_happy`,
      idle: `${name}_idle_happy`,
    },
    evolving: {
      walking: `${name}_walk_happy`,
      idle: `${name}_idle_happy`,
    },
    had_visitor: {
      walking: `${name}_walk_normal`,
      idle: `${name}_idle_normal`,
    },
    hungry: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    lonely: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    sad: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    very_hungry: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    very_lonely: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    very_sad: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    dirty: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    pee: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    very_dirty: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    very_pee: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    want_to_go_out: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    sick: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    tooth_decay: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    scared: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    sleeping: {
      walking: `${name}_sleep`,
      idle: `${name}_sleep`,
    },
    dying: {
      walking: `${name}_walk_semizombie`,
      idle: `${name}_idle_semizombie`,
    },
    dead: {
      walking: `${name}_walk_zombie`,
      idle: `${name}_idle_zombie`,
    },
  };
}

export const animationNames: Record<
  PetSpecies,
  Record<PetState, Record<'walking' | 'idle', string>> &
    Record<AnimationState, string>
> = {
  ball: createAnimation('ball'),
  crab: createAnimation('crab'),
  dog: createAnimation('doggish'),
  starball: createAnimation('starball'),
  hamster: createAnimation('hamster'),
  turtle: createAnimation('turtle'),
  seaotter: createAnimation('seaotter'),
  cat: createAnimation('cat'),
  whale: createAnimation('whale'),
  giraffe: createAnimation('giraffe'),
};

export function getStatusAnimationName(status: PetState) {
  switch (status) {
    case 'dirty':
    case 'very_dirty':
      return 'dirty';

    case 'hungry':
    case 'very_hungry':
      return 'hungry';

    case 'lonely':
    case 'very_lonely':
      return 'lonely';

    case 'pee':
    case 'very_pee':
      return 'pee';

    case 'want_to_go_out':
      return 'want_out';

    case 'happy':
    case 'sick':
    case 'scared':
    case 'tooth_decay':
      return status;

    default:
      return null;
  }
}

export function getPetAnimation(
  { activeScript, action, species, ...params }: AppearanceParams,
  movement: 'idle' | 'walking',
) {
  const speciesAnimations = getSpeciesAnimations(species);
  const script = Conversation.ruleset.getScript(activeScript);
  const animationOverride = action ?? script?.animation;
  if (animationOverride) {
    let animation = speciesAnimations[animationOverride];
    if (!animation) {
      captureGenericError(
        `Animation not defined for species ${species}, animation ${animation}`,
        null,
      );
      animation = speciesAnimations.normal.idle;
    }
    return animation;
  }

  const { user } = StateObserver.getState();
  const isVisiting = Visit.getters(user).isVisiting();
  const petState: PetState =
    script?.expression ?? Pet.ruleset.getPetState(params, params, isVisiting);
  let movementAnimations = speciesAnimations[petState];
  if (!movementAnimations) {
    captureGenericError(
      `Animation not defined for species ${species}, state ${petState}`,
      null,
    );
    movementAnimations = speciesAnimations.normal;
  }

  return movementAnimations[movement];
}

export function getSpeciesAnimations(species: PetSpecies) {
  const speciesAnimations = animationNames[species];
  if (speciesAnimations) {
    return speciesAnimations;
  }

  captureGenericError(`Animation not defined for species ${species}`, null);
  return animationNames['dog'];
}
